'use client';

import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Search, UserMinus, RefreshCw, Trash2, Download, AlertTriangle, TrendingDown, Calendar, Mail } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import { unsubscribeService, type Unsubscribe } from '@/lib/services/mailflowService';

const REASONS = ['Too many emails', 'Not relevant', 'Never signed up', 'Prefer not to say', 'Other'];

export default function UnsubscribeManagerClient() {
  const [unsubscribes, setUnsubscribes] = useState<Unsubscribe[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reasonFilter, setReasonFilter] = useState('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [resubscribeTarget, setResubscribeTarget] = useState<Unsubscribe | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [stats, setStats] = useState({ total: 0, thisMonth: 0, lastMonth: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [data, statsData] = await Promise.all([
        unsubscribeService.getAll(),
        unsubscribeService.getStats(),
      ]);
      setUnsubscribes(data);
      setStats(statsData);
    } catch (err: any) {
      toast.error('Failed to load unsubscribes');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return unsubscribes.filter((u) => {
      const matchSearch = u.email.toLowerCase().includes(search.toLowerCase()) ||
        (u.campaignName || '').toLowerCase().includes(search.toLowerCase());
      const matchReason = reasonFilter === 'all' || u.reason === reasonFilter || (reasonFilter === 'no-reason' && !u.reason);
      return matchSearch && matchReason;
    });
  }, [unsubscribes, search, reasonFilter]);

  const handleResubscribe = async () => {
    if (!resubscribeTarget) return;
    try {
      await unsubscribeService.resubscribe(resubscribeTarget.id, resubscribeTarget.email);
      setUnsubscribes((prev) => prev.filter((u) => u.id !== resubscribeTarget.id));
      setStats((s) => ({ ...s, total: Math.max(0, s.total - 1) }));
      toast.success(`${resubscribeTarget.email} resubscribed successfully`);
      setResubscribeTarget(null);
    } catch {
      toast.error('Failed to resubscribe');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await unsubscribeService.delete(deleteTarget);
      setUnsubscribes((prev) => prev.filter((u) => u.id !== deleteTarget));
      setStats((s) => ({ ...s, total: Math.max(0, s.total - 1) }));
      toast.success('Record removed');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete record');
    }
  };

  const handleExport = () => {
    const rows = [
      ['Email', 'Reason', 'Campaign', 'Unsubscribed At'],
      ...filtered.map((u) => [u.email, u.reason || 'No reason', u.campaignName || '—', new Date(u.unsubscribedAt).toLocaleDateString()]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'unsubscribes.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported unsubscribes CSV');
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((u) => u.id)));
  };

  const monthDelta = stats.lastMonth > 0
    ? Math.round(((stats.thisMonth - stats.lastMonth) / stats.lastMonth) * 100)
    : stats.thisMonth > 0 ? 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Unsubscribe Manager</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track and manage contacts who opted out of your campaigns
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadData} className="btn-secondary" title="Refresh">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button onClick={handleExport} className="btn-secondary">
            <Download size={15} /> Export CSV
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, rgba(91,53,232,0.12), rgba(91,53,232,0.06))' }}>
            <UserMinus size={18} style={{ color: '#5b35e8' }} />
          </div>
          <div>
            <p className="metric-label">Total Unsubscribes</p>
            <p className="text-2xl font-bold tabular-nums text-foreground">{stats.total.toLocaleString()}</p>
          </div>
        </div>
        <div className="card p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-amber-50">
            <Calendar size={18} className="text-amber-600" />
          </div>
          <div>
            <p className="metric-label">This Month</p>
            <p className="text-2xl font-bold tabular-nums text-foreground">{stats.thisMonth.toLocaleString()}</p>
          </div>
        </div>
        <div className={`card p-5 flex items-center gap-4 ${monthDelta > 20 ? 'ring-1 ring-red-200' : ''}`}>
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${monthDelta > 20 ? 'bg-red-50' : 'bg-green-50'}`}>
            <TrendingDown size={18} className={monthDelta > 20 ? 'text-red-600' : 'text-green-600'} />
          </div>
          <div>
            <p className="metric-label">vs Last Month</p>
            <p className={`text-2xl font-bold tabular-nums ${monthDelta > 20 ? 'text-red-600' : 'text-green-600'}`}>
              {monthDelta > 0 ? '+' : ''}{monthDelta}%
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border bg-card flex-1 max-w-sm">
          <Search size={14} className="text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by email or campaign…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none w-full"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { label: 'All Reasons', value: 'all' },
            { label: 'Too many emails', value: 'Too many emails' },
            { label: 'Not relevant', value: 'Not relevant' },
            { label: 'Never signed up', value: 'Never signed up' },
            { label: 'No reason', value: 'no-reason' },
          ].map((f) => (
            <button
              key={`rf-${f.value}`}
              onClick={() => setReasonFilter(f.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-150 ${
                reasonFilter === f.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-secondary hover:text-primary'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bulk bar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/20">
          <span className="text-sm font-semibold text-primary">{selected.size} selected</span>
          <div className="flex-1" />
          <button onClick={() => setSelected(new Set())} className="btn-ghost text-xs">Clear</button>
        </div>
      )}

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeDasharray="30 70" />
              </svg>
              <p className="text-sm text-muted-foreground">Loading unsubscribes…</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={selected.size === filtered.length && filtered.length > 0}
                      onChange={toggleAll}
                      className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                    />
                  </th>
                  {['Email', 'Reason', 'Campaign', 'Unsubscribed At', 'Actions'].map((col) => (
                    <th key={`uth-${col}`} className="px-4 py-3 text-left metric-label">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center">
                          <UserMinus size={22} className="text-green-500" />
                        </div>
                        <p className="text-sm font-semibold text-foreground">No unsubscribes found</p>
                        <p className="text-xs text-muted-foreground">Your list health looks great!</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((u) => (
                    <tr
                      key={u.id}
                      className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors duration-100 ${selected.has(u.id) ? 'bg-primary/5' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(u.id)}
                          onChange={() => toggleSelect(u.id)}
                          className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                            <Mail size={13} className="text-muted-foreground" />
                          </div>
                          <span className="text-sm font-medium text-foreground">{u.email}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {u.reason ? (
                          <Badge variant="neutral">{u.reason}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{u.campaignName || '—'}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                        {new Date(u.unsubscribedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setResubscribeTarget(u)}
                            title="Resubscribe contact"
                            className="w-7 h-7 rounded flex items-center justify-center hover:bg-green-50 transition-all duration-150"
                          >
                            <RefreshCw size={13} className="text-green-600" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(u.id)}
                            title="Remove record"
                            className="w-7 h-7 rounded flex items-center justify-center hover:bg-red-50 transition-all duration-150"
                          >
                            <Trash2 size={13} className="text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        {!loading && filtered.length > 0 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-border bg-muted/20">
            <p className="text-xs text-muted-foreground">
              Showing {filtered.length} of {unsubscribes.length} unsubscribes
            </p>
          </div>
        )}
      </div>

      {/* Resubscribe Confirm Modal */}
      <Modal open={!!resubscribeTarget} onClose={() => setResubscribeTarget(null)} title="Resubscribe Contact" size="sm">
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              This will remove <strong>{resubscribeTarget?.email}</strong> from the unsubscribe list and restore their active status. Only do this if they have explicitly re-consented.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setResubscribeTarget(null)} className="btn-secondary">Cancel</button>
            <button onClick={handleResubscribe} className="btn-primary">
              <RefreshCw size={14} /> Resubscribe
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Remove Record" size="sm">
        <div className="p-6 space-y-4">
          <p className="text-sm text-muted-foreground">
            Remove this unsubscribe record? The contact will remain unsubscribed — this only removes the log entry.
          </p>
          <div className="flex gap-3 justify-end">
            <button onClick={() => setDeleteTarget(null)} className="btn-secondary">Cancel</button>
            <button onClick={handleDelete} className="btn-danger">Remove Record</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
